<template>
    <Loader v-if="state === 'loading'" />
    <p v-else-if="state === 'forbidden'" class="fpc__note">You no longer have access to this lot.</p>
    <ErrorState v-else-if="state === 'unavailable'" message="Parking occupancy is temporarily unavailable." :retry="load" />
    <p v-else-if="state === 'unsupported'" class="fpc__note">Parked calls aren't available on this voice backend.</p>
    <ErrorState v-else-if="state === 'error'" :message="loadError" :retry="load" />
    <template v-else-if="snapshot">
        <!-- No "observed … ago" here: the store stamps observedAt at read time, so on a healthy feed it
             only counts up to the next poll. Data age is shown when it actually means something — below,
             on a failed refresh. -->
        <p class="fpc__summary">{{ snapshot.count }} parked</p>
        <p v-if="refreshFailed" class="fpc__stale">
            Refresh failed — showing data from {{ formatElapsed(Date.parse(snapshot.observedAt), now) }} ago
        </p>

        <table class="zs-table">
            <thead>
                <tr>
                    <th>Slot</th>
                    <th>Caller</th>
                    <th>Parked by</th>
                    <th>Waiting</th>
                    <th class="col-actions" />
                </tr>
            </thead>
            <tbody>
                <tr v-for="item in snapshot.items" :key="item.callId">
                    <td>{{ item.slotExtension }}</td>
                    <td>
                        <span v-if="item.callerName" class="callerId">{{ item.callerName }}</span>
                        <span class="number">{{ $filters.phone(item.callerNumber) }}</span>
                    </td>
                    <td>{{ parkedByLabel(item) }}</td>
                    <td>{{ formatElapsed(Date.parse(item.parkedAt), now) }}</td>
                    <td class="col-actions">
                        <div class="fpc__row-actions">
                            <button v-if="retrievable" type="button" class="zs-btn zs-btn--sm" @click="emit('retrieve', item)">
                                <i class="fa-regular fa-phone-arrow-down-left" />
                                <span>Retrieve</span>
                            </button>
                            <button v-if="showCallLink" type="button" class="zs-btn zs-btn--icon" v-tooltip="'View call'" @click="viewCall(item)">
                                <i class="fa-regular fa-phone" />
                            </button>
                        </div>
                    </td>
                </tr>
                <tr v-if="snapshot.items.length === 0">
                    <td colspan="5" class="zs-table__empty-cell">
                        <div class="zs-empty">
                            <i class="fa-regular fa-square-parking zs-empty__icon" />
                            <p class="zs-empty__title">No calls are parked in this lot</p>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
    </template>
</template>

<script lang="ts" setup>
import { dataFrom } from '@zyno-io/openapi-client-codegen';
import { formatError, handleError } from '@zyno-io/vue-foundation';
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

import { formatElapsed } from '@/modules/talk/helpers/duration';
import { formatExtension } from '@/modules/talk/helpers/formatters';
import { useLiveInvalidation } from '@/modules/talk/shared/composables/use-live-invalidation';
import {
    TalkParkingLotsApi,
    type IParkingLotCallResponse,
    type IParkingLotCallsResponse,
    type IParkingLotWithOccupancyResponse
} from '@/openapi-clients-generated/talk';
import ErrorState from '@/shared/components/error-state.vue';
import Loader from '@/shared/components/loader.vue';
import { DataCache } from '@/shared/data/cache';
import { apiErrorMessage, apiErrorStatus } from '@/shared/helpers/api-errors';

const props = withDefaults(
    defineProps<{
        lot: Pick<IParkingLotWithOccupancyResponse, 'id'>;
        /** Ticking "now" from the parent — one shared clock per screen rather than a timer per table. */
        now: number;
        /** Shows a per-row "Retrieve" button (dials the slot from the user's selected extension). */
        retrievable?: boolean;
        /** Shows the "View call" icon button, navigating to the CDR row. */
        showCallLink?: boolean;
        intervalMs?: number;
    }>(),
    { intervalMs: 20_000 }
);

const emit = defineEmits<{
    retrieve: [item: IParkingLotCallResponse];
    'view-call': [];
    /** Each newer snapshot as it lands — for a host that gates its own controls on the parked count. */
    snapshot: [snapshot: IParkingLotCallsResponse];
}>();

type State = 'loading' | 'ready' | 'unavailable' | 'unsupported' | 'forbidden' | 'error';

const snapshot = ref<IParkingLotCallsResponse>();
const state = ref<State>('loading');
const loadError = ref<string>();
/** A refresh (not the initial load) failed — the last-good snapshot stays on screen with a note. */
const refreshFailed = ref(false);

const router = useRouter();
const extensionsById = DataCache.getMapById('talkExtensions');

// `state` leaves 'loading' exactly once. A lot that never loaded (403 / 503 / unsupported / error)
// keeps showing that outcome while the periodic retries run behind it, rather than flashing a
// spinner every tick — on a screen full of lots with the occupancy store down that was one
// spinner per lot every 20 s.
async function load() {
    try {
        // 403 (access revoked mid-session), 503 (occupancy store down) and the legacy-backend 400 are
        // designed outcomes with their own views below, not faults worth a Sentry event every refresh tick.
        const next = dataFrom(
            await TalkParkingLotsApi.getParkingLotsGetLotCalls({ path: { id: props.lot.id }, meta: { ignoreSentryStatuses: [400, 403, 503] } })
        );
        // Reads overlap (an invalidation flush and a resync tick aren't serialized against each other);
        // the store's own observation time says which one is newer, so an older reply never wins.
        if (!snapshot.value || next.observedAt >= snapshot.value.observedAt) {
            snapshot.value = next;
            emit('snapshot', next);
        }
        state.value = 'ready';
        refreshFailed.value = false;
    } catch (err) {
        // A background refresh must never discard an already-successful snapshot.
        if (snapshot.value) {
            handleError(err);
            refreshFailed.value = true;
            return;
        }
        const status = apiErrorStatus(err);
        if (status === 403) {
            state.value = 'forbidden';
            return;
        }
        if (status === 503) {
            state.value = 'unavailable';
            return;
        }
        if (status === 400 && apiErrorMessage(err, '').includes('PARKING_OCCUPANCY_UNSUPPORTED_BACKEND')) {
            state.value = 'unsupported';
            return;
        }
        handleError(err);
        state.value = 'error';
        loadError.value = formatError(err);
    }
}

function parkedByLabel(item: IParkingLotCallResponse): string {
    if (!item.parkedByExtensionId) return '—';
    const ext = extensionsById.value[item.parkedByExtensionId];
    return ext ? formatExtension(ext) : '—';
}

function viewCall(item: IParkingLotCallResponse) {
    router.push({ name: 'talk-cdr', query: { callId: item.callId } });
    emit('view-call');
}

const liveInvalidation = useLiveInvalidation({
    keyFor: event => (event.event === 'parking-lot-updated' && event.payload.parkingLotId === props.lot.id ? event.payload.parkingLotId : null),
    onInvalidate: () => load(),
    onResync: () => load(),
    intervalMs: props.intervalMs
});

onMounted(async () => {
    await load();
    liveInvalidation.ready();
});
</script>

<style scoped>
@reference '#tw.css';

.fpc__summary {
    @apply text-sm text-ink-3 mb-3;
}

.fpc__stale {
    @apply text-xs text-ink-3 mb-2;
}

.fpc__note {
    @apply text-sm text-ink-3;
}

.col-actions {
    width: 1%;
    text-align: right;
    white-space: nowrap;
}

.fpc__row-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: var(--s-2);
}

/* Caller stacks a name over a (muted) number — mirrors cdr.vue's From column. */
.callerId {
    @apply block font-semibold;
}

.number {
    @apply block;
}

.number:not(:first-child) {
    color: var(--ink-3);
    @apply mt-1;
}
</style>
